/**
 * Savanna's curriculum flow, mapped onto the shapes the program pages render.
 *
 * eHub owns the program and course structure; Savanna owns the content inside
 * each course. `savannaCoursesForProgram` gives the curriculum id per course
 * (from `ClassLMSLocation.lmsCourseId`), and `savannaCurriculum(id).flow`
 * returns that content three levels deep - tracks, blocks, activities - with a
 * paywall flag on each.
 *
 * One curriculum becomes one `Course` and its BLOCKS become the `Module` rows.
 * The track in between is not rendered: a self-paced curriculum does not show
 * that level, and Savanna's own SDL glosses a block as "a block (module) of
 * activities". Tracks are flattened away on the way down, so a course's
 * modules are every block of every track, in order.
 *
 * The block is where the paywall line falls, which is what makes a module's
 * `partially-free` a fact rather than a summary: one block holds a handful of
 * activities and can be split across the line. Activities are rendered only by
 * the interactive tree; everywhere else they are read in bulk - to count, to
 * place a module on the free/partially-free/paid scale, and to derive
 * completion.
 *
 * Hours are absent on purpose: Savanna records no duration anywhere in the
 * flow - not on the flow, a track, a block or an activity - so `hours` stays
 * undefined and the pages fall back to activity counts.
 */
import {
  GetSavannaCoursesForProgramQuery,
  GetSavannaCurriculumQuery,
} from '@sandtech-hub-monorepo/ehub-client';

import {
  Activity,
  ActivityKind,
  Course,
  Module,
  ModuleAccess,
} from './programs';

/** One row of `savannaCoursesForProgram`. */
export type SavannaCourseLocation =
  GetSavannaCoursesForProgramQuery['savannaCoursesForProgram'][number];

export type SavannaCurriculum = NonNullable<
  GetSavannaCurriculumQuery['savannaCurriculum']
>;
export type SavannaFlow = NonNullable<SavannaCurriculum['flow']>;
export type SavannaTrack = SavannaFlow['tracks'][number];
export type SavannaBlock = SavannaTrack['blocks'][number];
export type SavannaActivity = SavannaBlock['activities'][number];

/**
 * Every block of a curriculum, in order, with the track level flattened away.
 *
 * Tracks are a grouping self-paced curricula do not display, so they are
 * unwrapped once here and nothing below this line needs to know they exist.
 * The query selects no field on a track for the same reason.
 */
const blocksOf = (flow: SavannaFlow | null | undefined): SavannaBlock[] =>
  flow?.tracks.flatMap((track) => track.blocks) ?? [];

/**
 * Whether every activity in a group is passed.
 *
 * An empty group would satisfy `every` vacuously, which would render as a
 * finished thing nobody has opened.
 */
const allPassed = (activities: SavannaActivity[]) =>
  activities.length > 0 && activities.every((activity) => activity.isPassed);

/**
 * Where a block sits on the free/paid scale, and so what the module row's
 * badge says.
 *
 * `underPaywallAll` is Savanna's own roll-up - "every activity below this is
 * behind the paywall" - so it settles `paid` without walking anything.
 * Anything else has to be read from the activities, because a block is
 * partially free when only SOME of them are paywalled: the learner can start
 * it but not finish it.
 */
const blockAccess = (block: SavannaBlock): ModuleAccess => {
  if (block.underPaywallAll) {
    return 'paid';
  }

  return block.activities.some((activity) => activity.underPaywall)
    ? 'partially-free'
    : 'free';
};

/**
 * Savanna's `type` is a plain String!, documented as one of three values.
 * Matching exactly and falling through to `other` keeps an unrecognised type
 * visible as unlabelled rather than silently rendering as a concept.
 */
const activityKind = (type: string): ActivityKind => {
  switch (type) {
    case 'Concept':
      return 'concept';
    case 'Project':
      return 'project';
    case 'EvaluationQuiz':
      return 'quiz';
    default:
      return 'other';
  }
};

const toActivity = (
  activity: SavannaActivity,
  hasLearner: boolean
): Activity => ({
  id: activity.id,
  // Savanna allows an unnamed activity; the row needs something to show.
  title: activity.name ?? 'Untitled activity',
  kind: activityKind(activity.type),
  paid: activity.underPaywall,
  completed: hasLearner ? activity.isPassed : undefined,
  // Nullable on Savanna's side, and left undefined rather than defaulted, so
  // a row with no path renders unlinked instead of pointing at the origin.
  path: activity.pathForLearner ?? undefined,
});

/**
 * One Savanna block, as the `Module` row both trees render.
 *
 * `hasLearner` is the caller's statement that the flow was resolved for a
 * specific person, which is the only condition under which `isPassed` means
 * anything. Savanna resolves `flow` from `sandId` first and falls back to the
 * batch or to the curriculum template, and those fallbacks answer
 * `isPassed: false` for everything - so without it, "not passed" and "nobody
 * was asked" are the same value.
 */
export const blockToModule = (
  block: SavannaBlock,
  hasLearner: boolean
): Module => ({
  id: block.id,
  // Savanna allows an unnamed block; the row needs something to show.
  title: block.name ?? 'Untitled module',
  access: blockAccess(block),
  activityCount: block.activities.length,
  completed: hasLearner ? allPassed(block.activities) : undefined,
  // Mapped always, read only by the interactive tree. Cheap, and it keeps one
  // mapping feeding both views rather than two that can disagree.
  activities: block.activities.map((activity) =>
    toActivity(activity, hasLearner)
  ),
});

/**
 * The `/ehub/authorize` link for a Start/Continue button: eHub's SSO
 * handshake into Savanna, landing the learner on `redirectTo` (this
 * activity's own `pathForLearner`) once authenticated, and on the curriculum
 * root otherwise.
 *
 * `redirectTo` is optional because Savanna does not promise a
 * `pathForLearner` for every activity - a caller without one still gets a
 * working, SSO'd link, just without the activity-level jump.
 *
 * Set through `URLSearchParams` rather than concatenated by hand, so
 * `redirectTo`'s own `/` and `?` are percent-encoded exactly once as the
 * value of this query's `redirect_to` param - a caller must not
 * pre-encode it, or Savanna would receive it double-encoded.
 */
export const savannaAuthorizeUrl = (
  authorizeUrl: string,
  curriculumId: string,
  redirectTo?: string
): string => {
  const params = new URLSearchParams({ context_curriculum_id: curriculumId });

  if (redirectTo) {
    params.set('redirect_to', redirectTo);
  }

  return `${authorizeUrl}?${params.toString()}`;
};

/**
 * Whether a course whose content has NOT been fetched should be presented as
 * needing a subscription.
 *
 * It cannot be derived: `savannaCourseToCourse` reads `locked` off the
 * blocks, and a course with no flow has none to read, so it maps to `false` -
 * "not locked" - which is the wrong answer to show. Both trees leave courses
 * unfetched, so both need a stand-in, and stating it once keeps them from
 * disagreeing.
 *
 * The rule is the GSA product's: the first course is the free one and the
 * rest come with the subscription. It is a claim about the product, not a
 * reading of Savanna, and it only ever surfaces for a learner without
 * coverage - `effectiveLocked` folds in `paidUnlocked` before anything is
 * drawn. A fetched course replaces it with the real answer.
 *
 * `index` is the 1-based position shown in the accordion badge.
 */
export const lockedBeforeFetch = (index: number) => index > 1;

/**
 * One eHub course, with its Savanna structure filled in.
 *
 * The title comes from the eHub course rather than the curriculum: the program
 * lists the course under a name staff chose, and the curriculum's own name is
 * Savanna-internal ("FREEMIUM VA-1").
 *
 * `index` is passed in because it is the position in the rendered list, not
 * `priority` - priority is the program's ordering and has gaps once a course is
 * removed.
 *
 * A null `flow` produces a course with no modules rather than no course. That
 * is the expected answer for a paced curriculum, and the accordion still has a
 * name and a number to show; hiding it would drop a course the program really
 * lists.
 */
export const savannaCourseToCourse = (
  location: SavannaCourseLocation,
  index: number,
  flow: SavannaFlow | null | undefined,
  hasLearner: boolean
): Course => {
  const blocks = blocksOf(flow);
  const modules = blocks.map((block) => blockToModule(block, hasLearner));
  const activities = blocks.flatMap((block) => block.activities);

  return {
    id: location.courseDefinitionId,
    index,
    title: location.courseName,
    curriculumId: location.curriculumId,
    // Course-level locked means "a subscription is needed to open anything
    // here", so it takes every module being fully paywalled - not Savanna's
    // `locked`, which is the stricter "paywalled AND this learner is behind on
    // payment" and is therefore false for everyone who has never paid, and
    // true for a GSA learner paying through Stripe rather than an ALX plan.
    locked: blocks.length > 0 && blocks.every((block) => block.underPaywallAll),
    modules,
    activityCount: flow?.totalActivities,
    // Only meaningful alongside a flow; without one there is nothing to be
    // free of. Counted over the same list so the two always sum to the
    // activities actually traversed.
    freeActivityCount: flow
      ? activities.filter((activity) => !activity.underPaywall).length
      : undefined,
    paidActivityCount: flow
      ? activities.filter((activity) => activity.underPaywall).length
      : undefined,
    // Counted over the same traversal as the free/paid split above, so every
    // number on the course header describes the same set of activities.
    progress:
      hasLearner && activities.length > 0
        ? {
            completedActivities: activities.filter(
              (activity) => activity.isPassed
            ).length,
            totalActivities: activities.length,
          }
        : undefined,
  };
};
